import { ErrorBoundary } from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Outlet, RouteObject, RouterProvider, createBrowserRouter } from 'react-router-dom';

import AuthRouter from '@auth/AuthRouter';
import AuthProvider from '@auth/contexts/AuthProvider';
import ErrorFallback from '@core/components/ErrorFallback';
import AppInitializer from '@core/components/navigation/AppInitializer';
import ToastProvider from '@core/components/Toast';
import CoreRouter from '@core/CoreRouter';
import CrmRouter from '@crm/CrmRouter';
import DashboardRouter from '@dashboard/DashboardRouter';
import SettingsRouter from '@settings/SettingsRouter';

import MessagingRouter from './modules/messaging/MessagingRouter';

// Query client configuration
const queryClient = new QueryClient({
  defaultOptions: {
    mutations: {
      retry: false,
    },
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

// Application routes configuration
const AppRoutes: RouteObject[] = [
  {
    path: '/',
    element: <RootProviders />,
    // All routes that need to be inside the root providers
    children: [CoreRouter, AuthRouter, DashboardRouter, CrmRouter, SettingsRouter, MessagingRouter],
  },
];

const router = createBrowserRouter(AppRoutes);

// Root component with core providers
function RootProviders() {
  return (
    <AuthProvider>
      <AppInitializer>
        <Outlet />
      </AppInitializer>
    </AuthProvider>
  );
}

// Main application component
function App() {
  return (
    // Error boundary to catch errors and display a fallback UI
    <ErrorBoundary
      fallback={({ error, resetError }) => (
        <ErrorFallback error={error as Error} onReset={resetError} />
      )}
    >
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        <ToastProvider />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
